import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Link as RouterLink } from '@tanstack/react-router';
import { Banner, Button, Cluster, Skeleton, Stack, Text } from '../ui';
import { ApiError } from '../lib/api';

/**
 * A single primary action for a teaching empty state — EITHER a click handler (e.g. open
 * a create dialog) OR a typed router destination (renders the button as a `<Link>`).
 * Provide one of `onClick` / `to`, not both.
 */
export interface EmptyStateAction {
  label: string;
  /** Click handler (mutually exclusive with `to`). */
  onClick?: () => void;
  /** Router destination, e.g. '/app/agents' — renders the button as a router link. */
  to?: string;
  params?: Record<string, string>;
  testId?: string;
}

/**
 * A rich, teaching empty state: WHAT this surface is, WHY it matters, and the one action
 * that fills it. Passed to `QueryState` via `emptyState`; rendered when the `empty`
 * predicate is true. Keep it quiet — an optional muted icon, a title, a short one-line
 * description, and at most one primary action.
 */
export interface EmptyStateContent {
  /** Small muted icon shown above the title. Optional; keep it understated. */
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: EmptyStateAction;
}

/** Centered, generously-spaced teaching empty state. UI primitive. */
function EmptyState({ content }: { content: EmptyStateContent }) {
  const { icon, title, description, action } = content;
  return (
    <Stack
      gap="2"
      align="center"
      data-testid="empty-state"
      style={{ paddingBlock: '4rem', paddingInline: '1.5rem', textAlign: 'center' }}
    >
      {icon != null && (
        <Text as="div" aria-hidden tone="muted" style={{ display: 'flex', fontSize: '2.5rem', lineHeight: 1 }}>
          {icon}
        </Text>
      )}
      <Text weight="semibold" size="lg" data-testid="empty-state-title">
        {title}
      </Text>
      {description != null && (
        <Text
          tone="secondary"
          size="sm"
          data-testid="empty-state-description"
          style={{ maxWidth: '46ch', textWrap: 'pretty' }}
        >
          {description}
        </Text>
      )}
      {action != null &&
        (action.to != null ? (
          <Button
            // Router Link as the button element; typed-route props bypass the button's
            // prop types via the same cast the legacy MUI version used.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...({ as: RouterLink, to: action.to, params: action.params } as any)}
            data-testid={action.testId}
            style={{ marginTop: '0.5rem' }}
          >
            {action.label}
          </Button>
        ) : (
          <Button onClick={action.onClick} data-testid={action.testId} style={{ marginTop: '0.5rem' }}>
            {action.label}
          </Button>
        ))}
    </Stack>
  );
}

/**
 * Default content-loading placeholder: a few list-row skeletons that roughly match the
 * common list shape, so the layout holds its ground and doesn't jump when data lands.
 * Preferred over a centre-of-content spinner for content areas (product-register
 * guidance). Callers with a different shape can pass their own via `loading`.
 */
function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <Stack gap="3" data-testid="query-loading-skeleton" aria-busy style={{ paddingBlock: '0.5rem' }}>
      {Array.from({ length: rows }, (_, i) => (
        <Cluster key={i} gap="3" align="center" wrap={false}>
          <Skeleton variant="circle" width="28px" height="28px" />
          <div style={{ flexGrow: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="70%" />
          </div>
        </Cluster>
      ))}
    </Stack>
  );
}

/**
 * Renders loading / error / empty states for a TanStack Query, delegating to
 * `children(data)` once loaded. Keeps every screen from re-implementing the same three
 * states. UI primitive.
 *
 * While loading, a skeleton placeholder (`ListSkeleton`) holds the layout so it doesn't
 * jump when data lands. A caller whose content isn't list-shaped — or a tiny inline area
 * where a spinner reads better — can override it via `loading` (e.g. `loading={null}` or
 * a custom node); default = skeleton rows.
 *
 * `empty` is the predicate deciding whether the loaded data is empty. When it is, a
 * screen may pass `emptyState` to TEACH — a title, one-line description and optional
 * primary action. Without `emptyState`, a quiet default is shown so screens not yet
 * wired don't regress.
 */
export function QueryState<T>({
  query,
  children,
  empty,
  emptyState,
  loading,
}: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  empty?: (data: T) => boolean;
  emptyState?: EmptyStateContent;
  /** Override the default loading skeleton (e.g. a custom skeleton or a spinner). */
  loading?: ReactNode;
}) {
  if (query.isLoading) {
    return <>{loading === undefined ? <ListSkeleton /> : loading}</>;
  }
  if (query.isError) {
    const err = query.error;
    const message = err instanceof ApiError ? err.message : 'Something went wrong.';
    // A transient load failure must never strand the page — every screen inherits a
    // "Try again" that refetches the query in place (no hard reload). Wired centrally on
    // the query object so no call site has to opt in.
    return (
      <Banner
        tone="danger"
        style={{ marginBlock: '1rem' }}
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            data-testid="query-retry"
          >
            Try again
          </Button>
        }
      >
        {message}
      </Banner>
    );
  }
  const data = query.data as T;
  if (empty?.(data)) {
    if (emptyState) return <EmptyState content={emptyState} />;
    return (
      <Text tone="secondary" align="center" style={{ paddingBlock: '1.5rem' }}>
        Nothing here yet.
      </Text>
    );
  }
  return <>{children(data)}</>;
}
